import { createSlice } from "@reduxjs/toolkit";
import { RootState, StoreDispatch } from "../store";
import { chatService } from "src/network/chatService";

export type ChatMessageStatus = "sending" | "sent" | "error" | "typing";

export type ChatInputOption = {
  value: string;
  label: string;
};

export type ChatMessage = {
  id: string;
  content: string;
  sender: "user" | "ai";
  timestamp: string;
  status?: ChatMessageStatus;
  userMessageId?: string;
  options?: ChatInputOption[];
};

const initialState: {
  messages: ChatMessage[];
  isOpen: boolean;
  isSending: boolean;
  sessionId: string | null;
  hasInitialized: boolean;
} = {
  messages: [],
  isOpen: false,
  isSending: false,
  sessionId: null,
  hasInitialized: false
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChatOpen(state, action: { payload: boolean }) {
      state.isOpen = action.payload;
    },
    addUserMessage(state, action: { payload: { id: string; content: string } }) {
      state.messages.push({
        id: action.payload.id,
        content: action.payload.content,
        sender: "user",
        timestamp: new Date().toISOString(),
        status: "sending"
      });
      state.isSending = true;
    },
    addTypingMessage(state) {
      const existingTyping = state.messages.find((m) => m.sender === "ai" && m.status === "typing");

      if (existingTyping) return;
      state.messages.push({
        id: `typing-${Date.now()}`,
        content: "",
        sender: "ai",
        timestamp: new Date().toISOString(),
        status: "typing"
      });
    },
    removeTypingMessage(state) {
      state.messages = state.messages.filter((m) => !(m.sender === "ai" && m.status === "typing"));
    },
    setUserMessageSent(state, action: { payload: string }) {
      const msg = state.messages.find((m) => m.id === action.payload);

      if (msg) msg.status = "sent";
      state.isSending = false;
    },
    addAiMessage(state, action: { payload: { id: string; content: string; options?: ChatInputOption[] } }) {
      state.messages = state.messages.filter((m) => !(m.sender === "ai" && m.status === "typing"));
      state.messages.push({
        id: action.payload.id,
        content: action.payload.content,
        sender: "ai",
        timestamp: new Date().toISOString(),
        status: "sent",
        options: action.payload.options
      });
      state.isSending = false;
    },
    setSessionId(state, action: { payload: string | null }) {
      state.sessionId = action.payload;
    },
    setChatInitialized(state) {
      state.hasInitialized = true;
    },
    setUserMessageError(state, action: { payload: string }) {
      const msg = state.messages.find((m) => m.id === action.payload);

      if (msg) msg.status = "error";
      state.messages = state.messages.filter((m) => !(m.sender === "ai" && m.status === "typing"));
      state.isSending = false;
    },
    setUserMessageSending(state, action: { payload: string }) {
      const msg = state.messages.find((m) => m.id === action.payload);

      if (msg) msg.status = "sending";
      state.isSending = true;
    },
    clearChat(state) {
      state.messages = [];
      state.isSending = false;
      state.sessionId = null;
      state.hasInitialized = false;
    }
  }
});

export const {
  setChatOpen,
  addUserMessage,
  addTypingMessage,
  removeTypingMessage,
  setUserMessageSent,
  addAiMessage,
  setSessionId,
  setChatInitialized,
  setUserMessageError,
  setUserMessageSending,
  clearChat
} = chatSlice.actions;

function generateId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const sendMessageAction = (content: string, userMessageId?: string) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const id = userMessageId ?? generateId();
    const currentSessionId = getState().chat.sessionId;

    if (!userMessageId) {
      dispatch(addUserMessage({ id, content }));
    } else {
      dispatch(setUserMessageSending(id));
    }
    dispatch(addTypingMessage());

    try {
      const response = await chatService({
        message: content,
        intent: "CREATE_USER",
        session_id: currentSessionId ?? undefined
      });
      const { session_id: receivedSessionId, message, input_fields } = response.data.process_customer_query;
      const options = input_fields?.[0]?.options ?? undefined;

      dispatch(removeTypingMessage());
      if (!userMessageId) {
        dispatch(setUserMessageSent(id));
      }
      if (receivedSessionId) {
        dispatch(setSessionId(receivedSessionId));
      }
      dispatch(
        addAiMessage({
          id: generateId(),
          content: message,
          options
        })
      );
    } catch {
      dispatch(removeTypingMessage());
      dispatch(setUserMessageError(id));
    }
  };
};

export const initializeChatAction = (initialMessage: string) => {
  return (dispatch: StoreDispatch, getState: () => RootState) => {
    if (getState().chat.hasInitialized) {
      return;
    }

    dispatch(setChatInitialized());
    dispatch(sendMessageAction(initialMessage));
  };
};

export default chatSlice.reducer;
